import React from "react";

/**
 * Skeleton — thin wrapper around the global .shimmer utility.
 * Use for placeholder blocks while data is loading.
 */
export function Skeleton({ className = "", style, ...rest }) {
  return (
    <div
      className={`shimmer rounded-xl ${className}`}
      aria-hidden="true"
      style={style}
      {...rest}
    />
  );
}

export function SkeletonText({ lines = 3, className = "" }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className="h-3"
          style={{ width: `${100 - i * 12}%` }}
        />
      ))}
    </div>
  );
}

/** Dashboard team skeleton: matches the real layout structure so there's no CLS on fill. */
export function DashboardSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="dashboard-loading">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="space-y-3">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-3 w-32" />
        </div>
        <Skeleton className="h-11 w-32 rounded-full" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="card-surface rounded-2xl p-5 space-y-3">
            <Skeleton className="aspect-square w-full rounded-xl" />
            <Skeleton className="h-3 w-1/2" />
            <Skeleton className="h-3 w-3/4" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-2xl" />
        ))}
      </div>
      <div className="card-surface rounded-2xl p-6 space-y-4">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    </div>
  );
}

/** TeamPage skeleton mimicking the shield + roster grid. */
export function TeamPageSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-6" data-testid="team-page-loading">
      <Skeleton className="h-3 w-40" />
      <div className="card-surface rounded-3xl p-6 md:p-8 flex items-center gap-6 flex-wrap">
        <Skeleton className="w-24 h-24 md:w-32 md:h-32 rounded-2xl" />
        <div className="space-y-3 flex-1 min-w-[200px]">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-10 w-3/4 max-w-md" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
      <div className="card-surface rounded-2xl p-6 space-y-3">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="aspect-[16/10] w-full rounded-xl" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="card-surface rounded-2xl p-3 space-y-3">
            <Skeleton className="aspect-square w-full rounded-xl" />
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** AlboDoro skeleton: podium + award cards, on the dark hero backdrop. */
export function AlboDoroSkeleton() {
  return (
    <div className="space-y-14" data-testid="albo-doro-loading">
      {/* Podium */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <Skeleton className="w-10 h-10 rounded-xl" />
          <div className="space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-6 w-56" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 md:items-end">
          <Skeleton className="h-56 md:h-64 rounded-3xl" />
          <Skeleton className="h-64 md:h-80 rounded-3xl" />
          <Skeleton className="h-56 md:h-64 rounded-3xl" />
        </div>
      </div>
      {/* Awards grid */}
      <div>
        <div className="flex items-center gap-3 mb-6">
          <Skeleton className="w-10 h-10 rounded-xl" />
          <Skeleton className="h-6 w-48" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-3xl bg-slate-900/40 p-6 space-y-4">
              <Skeleton className="h-4 w-32" />
              <div className="flex items-center gap-4">
                <Skeleton className="w-14 h-14 rounded-xl" />
                <div className="space-y-2 flex-1">
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
              <Skeleton className="h-3 w-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Skeleton;
